import React, { useEffect, useCallback } from 'react';
import { atom, useStateXAtom, useStateXSetter } from '@cloudio-saas/statex';
import PageContainer from 'root/PageContainer';
import { pageAtom } from 'designer/atoms';
import { usePending } from '../PlatformHooks';
import WaveDots from 'utils/WaveDots';
import { DialogPopup } from 'components/Popup/DialogPopup';
import { Platform } from 'componentregistry';
import { Typography } from '@mui/material';
import Page from './PageJson';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useDataStore from 'store/useDataStore';
import { useUserTimeZone } from 'root/useUser';
import { convertToUserTz } from 'utils/dateUtils';
import { IoAsyncExports } from '@cloudio-saas/datasource-types';
import Icon from 'icons/Icon';

interface AsyncExportProps {
  platform: Platform;
}

export const exportPropsAtom = atom<{
  appUid: string;
  itemId?: string;
  parentPageId?: string;
  datasourceUid?: string;
  data?: Record<string, unknown>;
}>({
  path: ['transient', 'asyncExport'],
  defaultValue: { appUid: '' },
});

export default function AsyncExport(props: AsyncExportProps) {
  const { platform } = props;
  const set = useStateXSetter();
  const [exportProps, setExportProps] = useStateXAtom(exportPropsAtom);
  const { itemId, appUid, parentPageId, datasourceUid } = exportProps;
  const { data } = exportProps;
  const open = !!itemId;
  const [pending, setPending] = usePending();
  const pageId = `async-exports-${itemId}`;
  const userTimeZone = useUserTimeZone();
  let formatType = '';
  if (data?.format) {
    const format: { formatType: string } = data.format as {
      formatType: string;
    };
    formatType = format.formatType;
  }

  const store = useDataStore<IoAsyncExports>({
    appUid,
    pageId,
    ds: 'IoAsyncExports',
    alias: 'ioAsyncExportsAlias',
  });
  store.hasPagination = true;
  store.maxRows = 15;

  const onClose = () => {
    setExportProps({ appUid: '' });
  };

  const getPage = () => {
    return Page;
  };

  const setPageData = useCallback(() => {
    platform.setter(['$pageData', appUid, pageId, 'itemId'], itemId);
    platform.setter(['$pageData', appUid, pageId, 'pageCode'], parentPageId);
    platform.setter(['$pageData', appUid, pageId, 'formatType'], formatType);
    platform.setter(
      ['$pageData', appUid, pageId, 'datasourceUid'],
      datasourceUid,
    );
    platform.setter(
      ['$pageData', appUid, pageId, 'scheduleDate'],
      userTimeZone
        ? convertToUserTz({
            value: new Date().toISOString(),
            userTimeZone,
            showTime: true,
            platform,
            attr: undefined,
          })
        : new Date().toISOString(),
    );
    platform.setter(
      ['$pageData', appUid, pageId, 'tzid'],
      userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    platform.setter(
      ['$pageData', appUid, pageId, 'requestJson'],
      JSON.stringify(data, null, ' '),
    );
    store.query();
  }, [
    platform,
    appUid,
    pageId,
    itemId,
    parentPageId,
    formatType,
    datasourceUid,
    userTimeZone,
    data,
    store,
  ]);

  useEffect(() => {
    if (open && parentPageId && itemId) {
      platform.startAsyncTransition(async () => {
        set(pageAtom, getPage(), {
          params: {
            appUid,
            pageId,
          },
        });
        setPageData();
      }, setPending);
    }
  }, [
    appUid,
    itemId,
    open,
    pageId,
    parentPageId,
    platform,
    set,
    setPageData,
    setPending,
  ]);

  const content = pending ? (
    <WaveDots active />
  ) : (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer
        key={itemId}
        width={1430}
        height={720}
        appUid={appUid}
        pageId={pageId}
        roleUid={platform.roleUid}
        paramString=""
        embedded
      />
    </LocalizationProvider>
  );

  if (!open) return null;

  return (
    <DialogPopup
      platform={platform}
      appUid={appUid}
      popupHeight={740}
      open={open}
      popupWidth={1450}
      modal={true}
      onClose={onClose}
      pageId={pageId}
      itemId={itemId}
      bodyChildren={content}
      resizable={false}
      Header={
        <Typography variant="subtitle1" fontWeight={500} fontSize="20px">
          Async Export
        </Typography>
      }
      toolbar={
        <Icon
          iconName="times"
          onClick={onClose}
          height={20}
          width={20}
          data-tip="Close"
        />
      }
    />
  );
}
